/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */

import React, { useEffect, useRef, useState } from "react";
import { graphql } from "gatsby";
import tw, { css } from "twin.macro";
import { useFlexSearch } from "react-use-flexsearch";
import { useSearch } from "~hooks";
import Grid from "~styles/Grid.jsx";
import * as T from "~styles/Typography.jsx";
import { Button, Go, LineSwipe, Layout, SEO, WaveCanvas } from "~components";
import { EASING_CUBIC, Keyframes } from "~styles/Animations.jsx";
import { MEDIA_QUERIES } from "~utils/css";
import { capitalizeString } from "~utils/helpers";

import { ReactComponent as ArrowUp } from "~assets/svg/arrow-up.svg";

const Search = ({ data, location }) => {
  // ---------------------------------------------------------------------------
  // context / ref / state

  const topRef = useRef(null);

  const { searchTerm, setSearchTerm } = useSearch();

  const [results, setResults] = useState([]);

  // ---------------------------------------------------------------------------
  // variables

  const {
    localSearchArticles,
    localSearchEvents,
    localSearchNews,
    localSearchPages
  } = data;

  const [searchQuery, setSearchQuery] = useState(``);

  const articleResults = useFlexSearch(
    searchQuery,
    localSearchArticles?.index,
    localSearchArticles?.store
  );
  const eventResults = useFlexSearch(
    searchQuery,
    localSearchEvents?.index,
    localSearchEvents?.store
  );
  const newsResults = useFlexSearch(
    searchQuery,
    localSearchNews?.index,
    localSearchNews?.store
  );
  const pageResults = useFlexSearch(
    searchQuery,
    localSearchPages?.index,
    localSearchPages?.store
  );

  // ---------------------------------------------------------------------------
  // methods

  const backToTop = () => {
    if (typeof window === `undefined`) {
      return;
    }

    topRef.current.scrollIntoView({ behavior: `smooth` });
  };

  // ---------------------------------------------------------------------------
  // lifecycle

  useEffect(() => {
    setSearchQuery(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setResults([
      ...articleResults,
      ...eventResults,
      ...newsResults,
      ...pageResults
    ]);
  }, [articleResults, eventResults, newsResults, pageResults]);

  // ---------------------------------------------------------------------------
  // render

  return (
    <>
      <Layout>
        <section
          ref={topRef}
          css={[
            css`
              background: #ffffff;

              @media screen and (min-width: 1025px) {
                height: 720px;
              }
            `,
            tw`w-full relative flex items-end pt-48 md:pt-0 pb-16 md:pb-8 lg:pb-12 xl:pb-20`
          ]}
        >
          <WaveCanvas />

          <Grid _css={[tw`z-20`]}>
            <header
              css={[
                tw`col-span-12 md:col-span-9 lg:col-span-8 xl:col-span-7 mb-12 md:mb-12 lg:mb-16 xl:mb-24`
              ]}
            >
              <div css={[tw`relative flex items-center mb-6 md:mb-4`]}>
                <T.Head
                  _css={[
                    css`
                      ${Keyframes(`appearDown`, {
                        delay: `300ms`,
                        settings: `1s ${EASING_CUBIC} forwards`
                      })}
                    `
                  ]}
                  font="4"
                  level="1"
                >
                  Search results for
                </T.Head>

                <div css={[tw`w-32 relative block ml-5`]}>
                  <LineSwipe color="#84bbd9" delay={500} duration={2000} />
                </div>
              </div>

              <input
                css={[
                  css`
                    background: transparent;
                    border: none;

                    ${Keyframes(`appearDown`, {
                      delay: `500ms`,
                      settings: `1s ${EASING_CUBIC} forwards`
                    })}
                  `,
                  tw`w-full relative block font-bold font-head md:text-h1-md text-h1`
                ]}
                onChange={(e) => {
                  setSearchTerm(e.currentTarget.value);
                  setSearchQuery(e.currentTarget.value);
                }}
                placeholder="Start your search here"
                type="text"
                value={searchTerm}
              />

              <div
                css={[
                  tw`relative flex items-center justify-between mt-4 md:mt-20`
                ]}
              >
                <T.Body
                  _css={[
                    css`
                      ${Keyframes(`appearDown`, {
                        delay: `500ms`,
                        settings: `1s ${EASING_CUBIC} forwards`
                      })}
                    `,
                    tw`text-blue-turq`
                  ]}
                  font="2"
                >
                  {`${results?.length?.toString()} results`}
                </T.Body>
              </div>
            </header>
          </Grid>
        </section>

        {results?.[0] && (
          <section css={[tw`w-full relative pb-24 md:pb-48`]}>
            <Grid>
              <div css={[tw`col-span-12 relative border-t border-blue-deep`]} />

              {results.map((result) => {
                let url = `/${result.slug}`;

                if (result?._type && !result?._type !== `page`) {
                  switch (result._type) {
                    case `article`:
                      url = `/frc-research/${result.slug}`;
                      break;

                    case `event`:
                      url = `/events/${result.slug}`;
                      break;

                    default:
                      url = `/${result._type}/${result.slug}`;
                      break;
                  }
                }

                return (
                  <article
                    key={`search-result-${result?.title}`}
                    css={[
                      tw`col-span-12 md:col-span-7 col-start-1 relative pt-12 pb-12 text-blue-deep`
                    ]}
                  >
                    <header>
                      <T.Body _css={[tw`mb-3`]} font="h6">
                        {result?.tag?.title || `Page`}
                      </T.Body>

                      <T.Head _css={[tw`font-bold`]} font="3" level="2">
                        {result.title}
                      </T.Head>
                    </header>

                    {result?.excerpt && (
                      <T.Body _css={[tw`mt-6`]} font="2">
                        {result.excerpt}
                      </T.Body>
                    )}

                    <Go to={url}>
                      <Button
                        _css={[tw`w-48 h-12 mt-8 px-6 border`]}
                        color="blue-deep"
                        small
                        text={`View ${
                          capitalizeString(result?._type) || `Page`
                        }`}
                      />
                    </Go>
                  </article>
                );
              })}
            </Grid>
          </section>
        )}

        <div css={[tw`absolute block bottom-8 right-0 z-30 pb-24`]}>
          <button
            type="button"
            css={[
              tw`w-28 h-16 z-30 bg-black`,
              css`
                svg {
                  margin: auto;
                  transition: 0.3s ease transform;
                }

                ${MEDIA_QUERIES.hoverable} {
                  &:hover {
                    svg {
                      transform: translate3d(-1.5rem, 0, 0);
                    }
                  }
                }
              `
            ]}
            onClick={backToTop}
            label="back-to-top"
          >
            <ArrowUp
              fill="white"
              css={[
                css`
                  display: absolute;
                  height: 50%;
                `
              ]}
            />
          </button>
        </div>
      </Layout>
    </>
  );
};

export default Search;

export const query = graphql`
  query Search {
    localSearchArticles {
      index
      store
    }
    allSanityArticle {
      edges {
        node {
          slug {
            current
          }

          title
          excerpt

          tag {
            title
          }
        }
      }
    }

    localSearchEvents {
      index
      store
    }
    allSanityEvent {
      edges {
        node {
          slug {
            current
          }

          title
          excerpt

          tag {
            title
          }
        }
      }
    }

    localSearchNews {
      index
      store
    }
    allSanityNews {
      edges {
        node {
          slug {
            current
          }

          title
          excerpt

          tag {
            title
          }
        }
      }
    }

    localSearchPages {
      index
      store
    }
    allSanityPage {
      edges {
        node {
          slug {
            current
          }

          title

          pagebuilder {
            sections {
              ... on SanityImageArticleSection {
                _key
                _type
                heading
                body
              }

              ... on SanityTextGridButtonSection {
                _type
                heading
                body

                content {
                  heading
                  body
                }
              }

              ... on SanityTabsSection {
                _type

                heading
                body
              }
            }
          }
        }
      }
    }
  }
`;
